(() => {
  const STORAGE_KEY = 'gmGarageVehicle';
  const ANY_VALUE = 'Todas';
  const selectors = {
    root: '[data-gm-garage]',
    drawer: '[data-gm-garage-drawer]',
    open: '[data-gm-garage-open]',
    close: '[data-gm-garage-close]',
    form: '[data-gm-garage-form]',
    brand: '[data-gm-garage-brand]',
    model: '[data-gm-garage-model]',
    year: '[data-gm-garage-year]',
    subModel: '[data-gm-garage-sub-model]',
    catalog: '[data-gm-garage-catalog]',
    product: '[data-gm-fitment-product]',
    status: '[data-gm-garage-status]',
    panel: '[data-gm-fitment-panel]'
  };

  const normalize = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  const toArray = (value) => {
    if (value == null || value === '') return [];
    if (Array.isArray(value)) return value.flatMap(toArray);
    if (typeof value === 'object') {
      if ('value' in value) return toArray(value.value);
      if ('label' in value) return toArray(value.label);
      return [];
    }
    return String(value)
      .split(/\|\||[,;•]/)
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const parseJsonAttr = (element, name) => {
    try {
      return JSON.parse(element.getAttribute(name) || 'null');
    } catch (_error) {
      return element.getAttribute(name);
    }
  };

  const parseYearRanges = (values) =>
    toArray(values).map((value) => {
      const match = String(value).match(/(\d{4})(?:\D+(\d{4}))?/);
      if (!match) return null;
      const start = Number(match[1]);
      const end = Number(match[2] || match[1]);
      return { start: Math.min(start, end), end: Math.max(start, end) };
    }).filter(Boolean);

  const includesNormalized = (values, target) => {
    const normalizedTarget = normalize(target);
    if (!normalizedTarget) return false;
    return toArray(values).some((value) => {
      const normalizedValue = normalize(value);
      return normalizedValue === normalizedTarget || normalizedValue.includes(normalizedTarget) || normalizedTarget.includes(normalizedValue);
    });
  };

  const yearMatches = (values, selectedYear) => {
    const year = Number(selectedYear);
    if (!year) return false;
    const ranges = parseYearRanges(values);
    return ranges.length === 0 || ranges.some((range) => year >= range.start && year <= range.end);
  };

  const subModelMatches = (values, selectedSubModel) => {
    if (!selectedSubModel || selectedSubModel === ANY_VALUE) return true;
    const subModels = toArray(values);
    if (subModels.length === 0) return true;
    const normalizedTarget = normalize(selectedSubModel);
    return subModels.some((value) => {
      const normalizedValue = normalize(value);
      return normalizedValue === normalize(ANY_VALUE) || normalizedValue === normalizedTarget;
    });
  };

  const fitmentPriority = {
    match: 0,
    partial: 1,
    mismatch: 2,
    none: 3
  };

  const productFitment = (element) => ({
    brand: parseJsonAttr(element, 'data-gm-fitment-brand'),
    model: parseJsonAttr(element, 'data-gm-fitment-model'),
    year: parseJsonAttr(element, 'data-gm-fitment-year'),
    subModel: parseJsonAttr(element, 'data-gm-fitment-sub-model')
  });

  const sortFitmentLists = (vehicle) => {
    if (!vehicle) return;

    document.querySelectorAll('[data-gm-fitment-sort-list]').forEach((list) => {
      const productItems = Array.from(list.children).filter(
        (item) => item.matches(selectors.product) || item.querySelector(selectors.product),
      );
      if (productItems.length < 2) return;

      productItems.sort((a, b) => {
        const productA = a.matches(selectors.product) ? a : a.querySelector(selectors.product);
        const productB = b.matches(selectors.product) ? b : b.querySelector(selectors.product);
        const stateA = productA.getAttribute('data-gm-fitment-state') || matchProduct(productA, vehicle);
        const stateB = productB.getAttribute('data-gm-fitment-state') || matchProduct(productB, vehicle);
        return fitmentPriority[stateA] - fitmentPriority[stateB];
      });

      const fragment = document.createDocumentFragment();
      productItems.forEach((item) => fragment.appendChild(item));
      list.appendChild(fragment);
    });
  };

  const readVehicle = () => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    } catch (_error) {
      return null;
    }
  };

  const writeVehicle = (vehicle) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(vehicle));
    window.dispatchEvent(new CustomEvent('gm:garage:change', { detail: vehicle }));
  };

  const clearVehicle = () => {
    localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new CustomEvent('gm:garage:change'));
  };

  const vehicleLabel = (vehicle) => {
    if (!vehicle) return '';
    return [vehicle.brand, vehicle.model, vehicle.year, vehicle.subModel && vehicle.subModel !== ANY_VALUE ? vehicle.subModel : '']
      .filter(Boolean)
      .join(' ');
  };

  const fillOptions = (select, values, placeholder) => {
    select.innerHTML = '';
    const first = document.createElement('option');
    first.value = '';
    first.textContent = placeholder;
    select.append(first);
    values.forEach((value) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = value;
      select.append(option);
    });
  };

  const setupGarage = () => {
    const root = document.querySelector(selectors.root);
    if (!root) return;

    const drawer = root.querySelector(selectors.drawer);
    const form = root.querySelector(selectors.form);
    const brandSelect = root.querySelector(selectors.brand);
    const modelSelect = root.querySelector(selectors.model);
    const yearSelect = root.querySelector(selectors.year);
    const subModelSelect = root.querySelector(selectors.subModel);
    const label = root.querySelector('[data-gm-garage-trigger-label]');
    const triggerVehicle = root.querySelector('[data-gm-garage-trigger-vehicle]');
    const catalogNode = root.querySelector(selectors.catalog);
    const catalog = JSON.parse(catalogNode?.textContent || '{}');

    const modelsForBrand = () => Object.keys(catalog[brandSelect.value] || {});
    const configForModel = () => catalog[brandSelect.value]?.[modelSelect.value] || { years: [], subModels: [] };

    const updateModels = () => {
      fillOptions(modelSelect, modelsForBrand(), 'Selecciona modelo');
      fillOptions(yearSelect, [], 'Selecciona año');
      fillOptions(subModelSelect, [ANY_VALUE], 'Selecciona versión');
    };

    const updateModelConfig = () => {
      const config = configForModel();
      fillOptions(yearSelect, config.years || [], 'Selecciona año');
      fillOptions(subModelSelect, config.subModels || [ANY_VALUE], 'Selecciona versión');
    };

    const setFormVehicle = (vehicle) => {
      if (!vehicle) return;
      brandSelect.value = vehicle.brand || '';
      updateModels();
      modelSelect.value = vehicle.model || '';
      updateModelConfig();
      yearSelect.value = vehicle.year || '';
      subModelSelect.value = vehicle.subModel || ANY_VALUE;
    };

    fillOptions(brandSelect, Object.keys(catalog), 'Selecciona marca');
    const savedVehicle = readVehicle();
    if (savedVehicle) setFormVehicle(savedVehicle);

    const syncTrigger = (vehicle) => {
      const nextVehicle = vehicle || readVehicle();
      if (nextVehicle) {
        label.textContent = 'Mi garage';
        triggerVehicle.textContent = vehicleLabel(nextVehicle);
      } else {
        label.textContent = 'Mi garage';
        triggerVehicle.textContent = 'Agregar vehículo';
      }
    };

    const openDrawer = () => {
      drawer.hidden = false;
      document.querySelector('.section-header')?.classList.add('gm-garage-open');
      document.documentElement.classList.add('overflow-hidden');
      brandSelect.focus();
    };

    const closeDrawer = () => {
      drawer.hidden = true;
      document.querySelector('.section-header')?.classList.remove('gm-garage-open');
      document.documentElement.classList.remove('overflow-hidden');
    };

    document.querySelectorAll(selectors.open).forEach((button) => button.addEventListener('click', openDrawer));
    root.querySelectorAll(selectors.close).forEach((button) => button.addEventListener('click', closeDrawer));
    brandSelect.addEventListener('change', updateModels);
    modelSelect.addEventListener('change', updateModelConfig);
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const vehicle = {
        brand: brandSelect.value,
        model: modelSelect.value,
        year: yearSelect.value,
        subModel: subModelSelect.value || ANY_VALUE
      };
      writeVehicle(vehicle);
      syncTrigger(vehicle);
      closeDrawer();
    });
    root.querySelector('[data-gm-garage-clear]').addEventListener('click', () => {
      clearVehicle();
      form.reset();
      fillOptions(modelSelect, [], 'Selecciona modelo');
      fillOptions(yearSelect, [], 'Selecciona año');
      fillOptions(subModelSelect, [ANY_VALUE], 'Selecciona versión');
      syncTrigger();
      closeDrawer();
    });

    syncTrigger(savedVehicle);
  };

  const matchProduct = (element, vehicle) => {
    if (!vehicle) return 'none';
    const fitment = productFitment(element);
    const brand = includesNormalized(fitment.brand, vehicle.brand);
    const model = includesNormalized(fitment.model, vehicle.model);
    const year = yearMatches(fitment.year, vehicle.year);
    const subModel = subModelMatches(fitment.subModel, vehicle.subModel);
    if (brand && model && year && subModel) return 'match';
    if (brand && model) return 'partial';
    return 'mismatch';
  };

  const syncProducts = () => {
    const vehicle = readVehicle();
    const status = document.querySelector(selectors.status);
    if (status) {
      status.hidden = !vehicle;
      const text = status.querySelector('[data-gm-garage-status-vehicle]');
      if (text) text.textContent = vehicleLabel(vehicle);
    }

    document.querySelectorAll(selectors.product).forEach((element) => {
      const state = matchProduct(element, vehicle);
      element.setAttribute('data-gm-fitment-state', state);
      const badge = element.querySelector('[data-gm-fitment-badge]');
      if (badge) {
        badge.textContent = state === 'match' ? 'Compatible' : state === 'partial' ? 'Revisar versión' : '';
      }
    });

    sortFitmentLists(vehicle);

    document.querySelectorAll(selectors.panel).forEach((panel) => {
      if (!vehicle) {
        panel.hidden = true;
        return;
      }
      const owner = panel.closest(selectors.product) || document.querySelector(selectors.product);
      const state = owner ? matchProduct(owner, vehicle) : 'none';
      panel.hidden = false;
      const title = panel.querySelector('[data-gm-fitment-panel-title]');
      const text = panel.querySelector('[data-gm-fitment-panel-text]');
      if (title) {
        title.textContent = state === 'match'
          ? `Compatibilidad confirmada con ${vehicleLabel(vehicle)}`
          : state === 'partial'
            ? `Revisar calce con ${vehicleLabel(vehicle)}`
            : `No calza con ${vehicleLabel(vehicle)}`;
      }
      if (text) {
        text.textContent = state === 'match'
          ? vehicleLabel(vehicle)
          : state === 'partial'
            ? vehicleLabel(vehicle)
            : vehicleLabel(vehicle);
      }
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    setupGarage();
    syncProducts();
  });
  window.addEventListener('gm:garage:change', syncProducts);
  document.addEventListener('shopify:section:load', syncProducts);
  document.addEventListener('gm:predictive-search:rendered', syncProducts);
})();
